import React, { useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { type TooltipItem } from 'chart.js';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import { Chart } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';

const BAR_COLORS = [
  'rgb(68, 114, 196)',
  'rgb(47, 85, 151)',
  'rgb(125, 211, 252)',
  'rgb(16, 185, 129)',
  'rgb(245, 158, 11)',
  'rgb(139, 92, 246)',
  'rgb(236, 72, 153)',
  'rgb(100, 116, 139)',
];

export interface AssignmentGroupRow {
  groupName: string;
  count: number;
  slaBreachPct: number;
}

export interface KpiIncidentAssignmentGroupComboChartProps {
  rows: AssignmentGroupRow[];
  onGroupClick?: (groupName: string) => void;
  /** Click on the bar's count label to open ticket table. */
  onGroupCountClick?: (groupName: string) => void;
  panelTitle?: string;
  emptyMessage?: string;
  /** Default: breach % on the line axis. Use `compliance` for 100 − breach (SLA met proxy). */
  slaLineMode?: 'breach' | 'compliance';
  barDatasetLabel?: string;
  /** When set, every bar uses this color (e.g. solid KPI blue). */
  uniformBarColor?: string;
  /** Cross-filter: highlight selected assignment group (Bandwidth-style). */
  selectedGroupName?: string | null;
  selectedBarColor?: string;
}

const DEFAULT_TITLE = 'Incidents By Assignment Group w/ SLA Breach %';

const KpiIncidentAssignmentGroupComboChart: React.FC<KpiIncidentAssignmentGroupComboChartProps> = ({
  rows,
  onGroupClick,
  onGroupCountClick,
  panelTitle = DEFAULT_TITLE,
  emptyMessage = 'No incidents for the selected filters.',
  slaLineMode = 'breach',
  barDatasetLabel = 'Incident count',
  uniformBarColor,
  selectedGroupName = null,
  selectedBarColor = 'rgb(30, 58, 120)',
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const labels = rows.map((r) => r.groupName);
  const counts = rows.map((r) => r.count);
  const breachPcts = rows.map((r) => r.slaBreachPct);
  const linePcts = useMemo(
    () =>
      slaLineMode === 'compliance'
        ? breachPcts.map((b) => Math.round((100 - b) * 10) / 10)
        : breachPcts,
    [breachPcts, slaLineMode]
  );
  const lineDatasetLabel = slaLineMode === 'compliance' ? 'SLA Compliance %' : 'SLA Breach %';
  const maxLine = useMemo(() => Math.max(0, ...linePcts, 0), [linePcts]);
  const y1Max = useMemo(() => {
    if (slaLineMode === 'compliance') {
      if (maxLine <= 0) return 100;
      const padded = Math.min(100, maxLine * 1.05);
      return Math.min(100, Math.ceil(padded / 5) * 5);
    }
    if (maxLine <= 0) return 10;
    const padded = maxLine * 1.2;
    return Math.min(100, Math.ceil(padded / 5) * 5);
  }, [maxLine, slaLineMode]);

  if (!rows.length) {
    return (
      <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill>
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onGroupClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const idx = el.index;
    const row = rows[idx];
    if (!row || row.count <= 0) return;
    onGroupClick(row.groupName);
  };

  return (
    <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill>
      <div className="relative min-h-[400px] w-full flex-1">
        <Chart
          ref={chartRef}
          type="bar"
          onClick={onGroupClick ? handleChartClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: barDatasetLabel,
                data: counts,
                backgroundColor: uniformBarColor
                  ? labels.map((name) =>
                      selectedGroupName != null && name === selectedGroupName ? selectedBarColor : uniformBarColor
                    )
                  : labels.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]),
                borderRadius: 6,
                barThickness: 28,
                yAxisID: 'y',
                order: 1,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: '#111827',
                  font: { weight: 600, size: 10 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                  listeners: onGroupCountClick
                    ? ({
                        click: (ctx: DataLabelContext, event: any) => {
                          const idx = ctx.dataIndex;
                          const row = rows[idx];
                          if (!row || row.count <= 0) return false;
                          onGroupCountClick(row.groupName);
                          event?.native?.stopPropagation?.();
                          return true;
                        },
                      } as any)
                    : undefined,
                },
              },
              {
                label: lineDatasetLabel,
                data: linePcts,
                type: 'line',
                borderColor: 'rgb(100, 116, 139)',
                backgroundColor: 'transparent',
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 5,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: 'rgb(100, 116, 139)',
                pointBorderWidth: 2,
                fill: false,
                yAxisID: 'y1',
                order: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 14,
                  color: '#475569',
                  font: { weight: 700, size: 9 },
                  formatter: (value: number, ctx: DataLabelContext) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    return `${Number.isFinite(v) ? v.toFixed(1) : '0'}%`;
                  },
                },
              },
            ] as any,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            onHover: (event, elements) => {
              const canvas = event.native?.target;
              if (!(canvas instanceof HTMLElement)) return;
              const el = elements[0];
              const idx = el?.index;
              const row = idx !== undefined ? rows[idx] : undefined;
              canvas.style.cursor = onGroupClick && row && row.count > 0 ? 'pointer' : 'default';
            },
            layout: { padding: { top: 28, right: 10, left: 4, bottom: 8 } },
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                align: 'center',
                labels: {
                  boxWidth: 18,
                  boxHeight: 10,
                  padding: 14,
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    const barFill = uniformBarColor ?? 'rgb(68, 114, 196)';
                    return [
                      {
                        text: barDatasetLabel,
                        fillStyle: barFill,
                        strokeStyle: 'transparent',
                        lineWidth: 0,
                        hidden: false,
                        index: 0,
                        datasetIndex: 0,
                      },
                      {
                        text: lineDatasetLabel,
                        fillStyle: '#ffffff',
                        strokeStyle: 'rgb(100, 116, 139)',
                        lineWidth: 1,
                        hidden: !ds[1],
                        index: 1,
                        datasetIndex: 1,
                      },
                    ] as any;
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    if (ctx.dataset.label === lineDatasetLabel) {
                      return `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}%`;
                    }
                    return `${ctx.dataset.label}: ${ctx.raw}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 35 },
              },
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                border: { display: false },
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: {
                  color: '#9ca3af',
                  maxTicksLimit: 8,
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                min: 0,
                max: y1Max,
                border: { display: false },
                grid: { drawOnChartArea: false },
                ticks: {
                  stepSize: y1Max <= 20 ? 5 : undefined,
                  maxTicksLimit: 8,
                  callback: (v) => `${v}%`,
                  color: '#9ca3af',
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiIncidentAssignmentGroupComboChart;
